/**
 * Financial Data Download Handler
 *
 * Calls the GGG Finance overview download API and exports the result as CSV or Excel.
 * Used by the LiveFinance page's "Financial Data Download" modal.
 */
import { downloadGggFinanceOverview } from '@sg/services';
import { exportTableToCsv, type ICsvColumn } from '@sg/utils/exportTableToCsv';
import { writeFile } from '@sg/utils/downLoadFileUtils';
import { dealDownLoadData } from '@sg/components/Chart/Operations/dealDownLoadData';
import type { TMetricsDownloadFileFormat } from '@sg/pages/StudioManagement/component/MetricsDownloadModal/config';

interface IDownloadFinanceParams {
  startDate: string;
  endDate: string;
  byPlatform: boolean;
  fileFormat: TMetricsDownloadFileFormat;
}

interface IFinanceDownloadRow {
  project: string;
  date: string;
  platform: string;
  revenue_after_refund: number | string;
  base_game_units_after_refund: number | string;
  mau: number | string;
  arpu: number | string;
  [key: string]: unknown;
}

/** Sort rows by date (newest first), then by project, then by platform. */
const sortRowsByDate = (rows: IFinanceDownloadRow[]): IFinanceDownloadRow[] =>
  [...rows].sort((a, b) => {
    // Descending date: newest → oldest
    const dateCmp = (b.date || '').localeCompare(a.date || '');
    if (dateCmp !== 0) return dateCmp;
    const projectCmp = (a.project || '').localeCompare(b.project || '');
    if (projectCmp !== 0) return projectCmp;
    return (a.platform || '').localeCompare(b.platform || '');
  });

/** Remove rows where all four metric columns are null/undefined. */
const filterEmptyRows = (rows: IFinanceDownloadRow[]): IFinanceDownloadRow[] =>
  rows.filter((row) => {
    const hasRevenue = row.revenue_after_refund != null;
    const hasUnits = row.base_game_units_after_refund != null;
    const hasMau = row.mau != null;
    const hasArpu = row.arpu != null;
    return hasRevenue || hasUnits || hasMau || hasArpu;
  });

const buildColumns = (byPlatform: boolean): ICsvColumn[] => {
  const columns: ICsvColumn[] = [
    { header: 'Project', key: 'project' },
    { header: 'Date', key: 'date' },
  ];
  if (byPlatform) {
    columns.push({ header: 'Platform', key: 'platform' });
  }
  columns.push(
    { header: 'Revenue After Refund ($)', key: 'revenue_after_refund' },
    { header: 'Base Game Units After Refund', key: 'base_game_units_after_refund' },
    { header: 'MAU', key: 'mau' },
    { header: 'ARPU ($)', key: 'arpu' },
  );
  return columns;
};

const buildExcelColumns = (byPlatform: boolean): any[] => {
  const columns: any[] = [
    { header: 'Project', key: 'project' },
    { header: 'Date', key: 'date' },
  ];
  if (byPlatform) {
    columns.push({ header: 'Platform', key: 'platform' });
  }
  columns.push(
    { header: 'Revenue After Refund ($)', key: 'revenue_after_refund' },
    { header: 'Base Game Units After Refund', key: 'base_game_units_after_refund' },
    { header: 'MAU', key: 'mau' },
    { header: 'ARPU ($)', key: 'arpu' },
  );
  return columns;
};

export const downloadFinanceData = async (
  params: IDownloadFinanceParams,
): Promise<void> => {
  const { startDate, endDate, byPlatform, fileFormat } = params;

  const res = await downloadGggFinanceOverview({
    start_date: startDate,
    end_date: endDate,
    by_platform: byPlatform,
  });

  const rawList: IFinanceDownloadRow[] = res?.data?.list ?? res?.list ?? [];
  const filteredList = filterEmptyRows(rawList);
  const sortedList = sortRowsByDate(filteredList);

  const fileName = `Financial Data ${startDate}~${endDate}`;

  if (fileFormat === 'csv') {
    const columns = buildColumns(byPlatform);
    exportTableToCsv({ fileName, columns, data: sortedList });
    return;
  }

  // Excel export
  const columns = buildExcelColumns(byPlatform);
  await writeFile(
    dealDownLoadData({ data: sortedList, columns, name: fileName }),
    fileName,
  );
};
